import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchWithAuth } from '../libs/apiUtils'
import { 
  MagnifyingGlassIcon, 
  FolderIcon, 
  TagIcon, 
  TableCellsIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'

interface SearchResultItem {
  id: string
  name: string
  type: 'group' | 'category' | 'table'
  groupId?: string
  categoryId?: string
  groupName?: string
  categoryName?: string
  tableCount?: number
  isPublic?: boolean
}

export default function GlobalSearchBar() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [results, setResults] = useState<SearchResultItem[]>([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const roleSlug = localStorage.getItem('user_role_slug')
  const isConsumer = roleSlug === 'consumer' || roleSlug === 'user'

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)
    return () => clearTimeout(handler)
  }, [query])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([])
      setLoading(false)
      return
    }

    let isMounted = true
    async function fetchPreviewResults() {
      setLoading(true)
      try {
        const searchStr = debouncedQuery.trim()
        const backendUrl = process.env.REACT_APP_BACKEND_URL

        const [groupsRes, categoriesRes, tablesRes] = await Promise.all([
          fetchWithAuth(`${backendUrl}/api/groups`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 1, perPage: 5, search: searchStr }),
          }),
          fetchWithAuth(`${backendUrl}/api/categories`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 1, perPage: 5, search: searchStr }),
          }),
          fetchWithAuth(`${backendUrl}/api/tables`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 1, perPage: 5, search: searchStr }),
          })
        ])

        const [groupsData, categoriesData, tablesData] = await Promise.all([
          groupsRes.ok ? groupsRes.json() : { data: [] },
          categoriesRes.ok ? categoriesRes.json() : { data: [] },
          tablesRes.ok ? tablesRes.json() : { data: [] }
        ])

        if (!isMounted) return

        const combined: SearchResultItem[] = [
          ...(groupsData.data || []).map((g: any) => ({
            id: g.id,
            name: g.name,
            type: 'group' as const,
            tableCount: g.tableCount
          })),
          ...(categoriesData.data || []).map((c: any) => ({
            id: c.id,
            name: c.name,
            type: 'category' as const,
            groupId: c.groupId,
            groupName: c.groupName,
            tableCount: c.tableCount
          })),
          ...(tablesData.data || []).map((t: any) => ({
            id: t.id,
            name: t.name,
            type: 'table' as const,
            categoryId: t.categoryId,
            categoryName: t.categoryName,
            groupId: t.groupId,
            groupName: t.groupName,
            isPublic: t.isPublic
          }))
        ]

        setResults(combined)
        setIsOpen(true)
      } catch (err) {
        console.error('Failed to fetch search preview', err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    fetchPreviewResults()
    return () => { isMounted = false }
  }, [debouncedQuery])

  function handleItemClick(item: SearchResultItem) {
    setIsOpen(false)
    if (item.type === 'group') {
      navigate(`/data/${item.id}/category`)
    } else if (item.type === 'category') {
      if (item.groupId) {
        navigate(`/data/${item.groupId}/${item.id}/table`)
      } else {
        navigate(`/data/group`)
      }
    } else if (item.type === 'table') {
      if (isConsumer) {
        navigate(`/data/${item.id}`)
      } else if (item.groupId && item.categoryId) {
        navigate(`/data/${item.groupId}/${item.categoryId}/${item.id}`)
      } else {
        navigate(`/data/group`)
      }
    }
  }

  function handleSeeAll() {
    setIsOpen(false)
    navigate(`/search?q=${encodeURIComponent(query.trim())}`)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && query.trim()) {
      handleSeeAll()
    }
  }

  const previewItems = results.slice(0, 5)

  return (
    <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md mx-4" ref={containerRef}>
      <div className="relative flex items-center">
        <MagnifyingGlassIcon className="size-4 absolute left-3 text-neutral-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onFocus={() => { if (results.length > 0) setIsOpen(true) }}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Cari grup, kategori, atau tabel..."
          className="w-full pl-9 pr-8 py-1.5 bg-neutral-100/80 hover:bg-neutral-100 focus:bg-white border border-transparent focus:border-primary-300 rounded-full text-xs text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/10 transition-all"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]) }}
            className="absolute right-3 text-[10px] font-bold text-neutral-400 hover:text-neutral-600 bg-neutral-200 hover:bg-neutral-300 rounded-full size-4 flex items-center justify-center"
          >
            ✕
          </button>
        )}
      </div>

      {isOpen && (query.trim().length > 0) && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-neutral-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="p-2 border-b border-neutral-50 flex items-center justify-between">
            <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider px-2">Hasil Pencarian</span>
            {loading && <span className="text-[10px] text-primary-600 animate-pulse px-2">Memuat...</span>}
          </div>

          {!loading && previewItems.length === 0 ? (
            <div className="p-4 text-center text-xs text-neutral-400">
              Tidak ada data ditemukan untuk "{query}"
            </div>
          ) : (
            <div className="divide-y divide-neutral-50 max-h-80 overflow-y-auto">
              {previewItems.map((item) => (
                <button
                  key={`${item.type}-${item.id}`}
                  onClick={() => handleItemClick(item)}
                  className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-primary-50/50 transition-colors text-left group"
                >
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    {item.type === 'group' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 flex-shrink-0">
                        <FolderIcon className="size-3" />
                        GRUP
                      </span>
                    )}
                    {item.type === 'category' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100 flex-shrink-0">
                        <TagIcon className="size-3" />
                        KATEGORI
                      </span>
                    )}
                    {item.type === 'table' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 flex-shrink-0">
                        <TableCellsIcon className="size-3" />
                        TABEL
                      </span>
                    )}

                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-neutral-800 truncate group-hover:text-primary-600 transition-colors">
                        {item.name}
                      </p>
                      {item.groupName && (
                        <p className="text-[10px] text-neutral-400 truncate">
                          {item.groupName} {item.categoryName ? ` / ${item.categoryName}` : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  <ChevronRightIcon className="size-3.5 text-neutral-300 group-hover:text-primary-500 transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {query.trim().length > 0 && (
            <button
              onClick={handleSeeAll}
              className="w-full py-2.5 bg-neutral-50 hover:bg-primary-50 text-primary-600 text-xs font-bold border-t border-neutral-100 text-center transition-colors flex items-center justify-center gap-1"
            >
              Tampilkan lebih banyak
              <ChevronRightIcon className="size-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
